/**
 * Estimate the low-frequency illumination field of a luma buffer by
 * downsampling (box averaging) to a small grid and bilinearly upsampling
 * back. This is much cheaper than a large-radius Gaussian/box blur applied
 * at full resolution, while still capturing "this corner is in shadow, that
 * corner has a flash reflection" style uneven lighting.
 */
function estimateIllumination(
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  gridSize = 24,
): Float32Array {
  const gw = Math.max(2, Math.min(gridSize, width));
  const gh = Math.max(2, Math.min(gridSize, height));
  const sums = new Float32Array(gw * gh);
  const counts = new Float32Array(gw * gh);

  for (let y = 0; y < height; y++) {
    const gy = Math.min(gh - 1, Math.floor((y * gh) / height));
    const row = y * width;
    for (let x = 0; x < width; x++) {
      const gx = Math.min(gw - 1, Math.floor((x * gw) / width));
      const idx = gy * gw + gx;
      sums[idx] += luma[row + x];
      counts[idx]++;
    }
  }
  const grid = new Float32Array(gw * gh);
  for (let i = 0; i < grid.length; i++) grid[i] = counts[i] ? sums[i] / counts[i] : 128;

  // Bilinear upsample back to full resolution, sampling grid *cell centers*.
  const field = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    const gy = (y * gh) / height - 0.5;
    let gy0 = Math.floor(gy);
    const fy = gy - gy0;
    let gy1 = gy0 + 1;
    gy0 = Math.min(Math.max(gy0, 0), gh - 1);
    gy1 = Math.min(Math.max(gy1, 0), gh - 1);
    for (let x = 0; x < width; x++) {
      const gx = (x * gw) / width - 0.5;
      let gx0 = Math.floor(gx);
      const fx = gx - gx0;
      let gx1 = gx0 + 1;
      gx0 = Math.min(Math.max(gx0, 0), gw - 1);
      gx1 = Math.min(Math.max(gx1, 0), gw - 1);

      const v00 = grid[gy0 * gw + gx0];
      const v10 = grid[gy0 * gw + gx1];
      const v01 = grid[gy1 * gw + gx0];
      const v11 = grid[gy1 * gw + gx1];
      const top = v00 + (v10 - v00) * fx;
      const bottom = v01 + (v11 - v01) * fx;
      field[y * width + x] = top + (bottom - top) * fy;
    }
  }
  return field;
}

/**
 * Flatten uneven illumination (shadow on one side, glare hotspot on the
 * other) by re-centering every pixel around a fixed mid-gray relative to
 * its *local* illumination estimate instead of the frame's global exposure.
 * This is what lets the same pass recover both a dark corner and a
 * flash-blown corner of the same frame, which a single global
 * brightness/contrast adjustment cannot do.
 *
 * @param gain how strongly local detail is amplified relative to the local mean.
 */
export function flattenIllumination(
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  gain = 1.6,
): Uint8ClampedArray {
  const field = estimateIllumination(luma, width, height);
  const out = new Uint8ClampedArray(width * height);
  for (let i = 0; i < out.length; i++) {
    out[i] = 128 + (luma[i] - field[i]) * gain;
  }
  return out;
}

/**
 * Soft-knee compression of near-saturated highlights so a specular
 * reflection doesn't clip to flat 255 and erase the module edges under it.
 * Values below the knee are untouched; above it they're compressed
 * logarithmically towards 255.
 */
export function softClipHighlights(
  luma: Uint8ClampedArray,
  knee = 210,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(luma.length);
  const range = 255 - knee;
  for (let i = 0; i < luma.length; i++) {
    const v = luma[i];
    if (v <= knee) {
      out[i] = v;
    } else {
      const t = (v - knee) / range;
      out[i] = knee + range * Math.log1p(t * 4) / Math.log1p(4);
    }
  }
  return out;
}
